/**
 * SurfaceContext — propagates the active color context to a subtree.
 *
 * Two responsibilities:
 *
 *   1. A React context carrying the current dark or light value.
 *      Components that need to KNOW the context for non-color reasons
 *      (LogoAssemble picks a glyph fill, etc.) read it via
 *      useSurfaceContext().
 *
 *   2. A DOM wrapper that sets data-color-scheme on the subtree.
 *      MUI v6 CssVarsProvider emits two parallel CSS-var blocks scoped
 *      by that attribute:
 *
 *        :root,[data-color-scheme=dark]  { --ads-palette-X: dark-value; }
 *               [data-color-scheme=light] { --ads-palette-X: light-value; }
 *
 *      Setting the attribute on any DOM node flips ALL --ads-palette-*
 *      vars inside it via CSS inheritance. Components reading
 *      theme.palette.X.Y (which compiles to var(--ads-palette-X-Y))
 *      automatically pick up the subtree value — no per-component
 *      light-vs-inverse switching.
 *
 *      The wrapper uses display:contents so it is invisible to layout —
 *      children behave as if rendered at the parent level.
 *
 * Used by:
 *   - Modal (always wraps in <SurfaceContextProvider value="light">
 *     because modals are intrinsically light surfaces).
 *   - The Storybook preview decorator (wires the toolbar Context global).
 *   - Anywhere a designer wants a subtree to render in a different
 *     context than its parent.
 *
 * @format
 */

import {createContext, useContext, type ReactNode} from 'react';

import {Box} from '@mui/material';

export type SurfaceContext = 'dark' | 'light';

const Ctx = createContext<SurfaceContext>('dark');

// Earlier we used `display: contents` here to make the wrapper invisible
// to layout. CSS spec says custom properties inherit through
// contents-display elements, but in practice (MUI v6 + emotion + our
// CssVarsProvider setup) the cascade flip for OUR `--ads-palette-*`
// vars was unreliable: Modal stayed on the outer scheme even when the
// wrapper had `data-color-scheme="light"`. Switching to a plain
// wrapper `<div>` solves it. Layout impact is nil because Modal's
// parent is a flex backdrop or a single inline mount; the extra
// block container shrink-wraps to its content.

interface SurfaceContextProviderProps {
  value: SurfaceContext;
  children: ReactNode;
}

/**
 * Wrap a subtree so that:
 *   - useSurfaceContext() returns value for any descendant.
 *   - MUI CSS-var cascade resolves palette.X.Y against value color scheme
 *     (via data-color-scheme attribute).
 *
 * The wrapper element is a plain <div>. Layout impact is nil at
 * Modal-level mounts (centered flex backdrop, or single inline child).
 */
export function SurfaceContextProvider({value, children}: SurfaceContextProviderProps) {
  const inherited = useContext(Ctx);
  const sameAsParent = inherited === value;

  // When the requested context matches the surrounding one (or matches
  // the document's MUI scheme at the root), the data-color-scheme attribute
  // is already in effect via an ancestor — the cascade does the work and
  // we skip emitting a redundant wrapper div. The React context provider
  // is cheap and always wraps so useSurfaceContext() returns consistent
  // values regardless of structural shortcuts.
  if (sameAsParent) {
    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
  }

  return (
    <Ctx.Provider value={value}>
      <Box data-color-scheme={value}>{children}</Box>
    </Ctx.Provider>
  );
}

/**
 * Resolve the effective surface context for a component instance.
 * Pass the component prop; if it is undefined, fall back to the
 * surrounding React context, which itself defaults to dark.
 *
 *   const ctx = useSurfaceContext(props.context);
 */
export function useSurfaceContext(propValue?: SurfaceContext): SurfaceContext {
  const inherited = useContext(Ctx);
  return propValue ?? inherited;
}

/**
 * Return the CSS-var REFERENCE string for one of our surface tokens —
 * e.g. `surfaceVar('onSurface')` → `'var(--ads-palette-surface-onSurface)'`.
 *
 * Why this exists: MUI v6's CssVarsProvider does NOT convert custom
 * palette namespaces (surface, disabledStates, …) into var references
 * inside the `theme.palette.*` object at styled-callback read time. That
 * works for the built-in `primary` / `error` / etc., but not for our
 * additions. So `color: theme.palette.surface.onSurface` resolves to a
 * literal hex from the active scheme, emotion bakes it into the CSS rule,
 * and the SurfaceContextProvider's `data-color-scheme` cascade has nothing
 * to override.
 *
 * Using this helper writes `color: var(--ads-palette-surface-onSurface)`
 * into the CSS rule directly, so the var picks up the closest
 * `[data-color-scheme]` ancestor at paint time.
 */
export type SurfaceVarKey =
  | 'surface1'
  | 'surface2'
  | 'surface3'
  | 'onSurface'
  | 'onSurfaceLight'
  | 'onSurfaceMuted'
  | 'inverseSurface'
  | 'inverseSurface1'
  | 'inverseSurface2'
  | 'inverseSurface3'
  | 'inverseOnSurface'
  | 'inverseOnSurfaceLight'
  | 'inverseOnSurfaceMuted';

export const surfaceVar = (key: SurfaceVarKey): string => `var(--ads-palette-surface-${key})`;
